import { Divider, Stack, Typography } from '@ace-clublabs/shared-component-library';
import { CartProductCode, CurrentPayPlan, FlowType } from '@ace-clublabs/common-membership-api-types';
import { ReviewItem } from './ReviewItem';
import { useContext } from 'react';
import { MembershipAppStateContext } from '../MembershipAppState/MembershipAppState';
import { formatFullName, getMembershipTitle } from '../../utils/helpers';
import { ProductType } from '../../application/models/NewJoinSessionData';
import { associateTypeMap } from '../../utils/stringHelper';

export const RecurringItemsSection = () => {
  const { sessionData } = useContext(MembershipAppStateContext);
  const serviceLevelCost = sessionData?.priceCart?.items?.find((item) => item.code === CartProductCode.SERVICELEVEL);
  const isMonthly = sessionData.formData.billPlan === CurrentPayPlan.RECURRINGMONTHLY;
  const hasRV = sessionData.formData?.products?.includes(ProductType.RV);
  const membershipTitle = <>{getMembershipTitle(sessionData.formData.membershipLevel!, hasRV)} member</>;
  const hasMembershipDiscount = typeof serviceLevelCost?.discount === 'number' && serviceLevelCost?.discount > 0;
  const hasAssociates = sessionData.formData.associates && sessionData.formData.associates.length > 0;
  const familyUpgradeCost = sessionData?.priceCart?.items?.find((item) => item.code === CartProductCode.FAMILYCOST);

  const getPromotionTitle = (flowType: FlowType, hasMembershipDiscount?: boolean) => {
    switch (flowType) {
      case FlowType.EMPLOYEE:
        return 'Employee discount';
      case FlowType.T_MOBILE:
        return 'Year on Us by T-Mobile';
      default:
        if (hasMembershipDiscount) {
          return 'Membership discount';
        }
        return '';
    }
  };

  return (
    <Stack>
      <Stack direction="column" className="recurring-items-section" width="100%" gap="0.5rem">
        <Typography variant="b3Regular" className="bill-plan-title">
          {isMonthly ? 'Monthly membership' : 'Annual membership'}
        </Typography>

        {/* Service Level Cost */}
        <ReviewItem
          className="benefit-level"
          title={membershipTitle}
          subtitle={getPromotionTitle(sessionData?.flowType!, hasMembershipDiscount)}
          {...serviceLevelCost!}
        />

        {/* Family members with different gap */}
        <Stack gap="0.25rem">
          <ReviewItem
            className="primary-member"
            title={`• ${formatFullName(sessionData.formData.firstName, sessionData.formData.lastName)}`}
            subtitle="primary"
            mask={true}
          />

          {/* Family member section */}
          {hasAssociates &&
            sessionData.formData.associates?.map((associate, index) => {
              const associatePriceCart = sessionData.priceCart?.items?.find((a) => a.id === associate.virtualId);
              return (
                <ReviewItem
                  key={index}
                  title={`• ${formatFullName(associate.firstName, associate.lastName)}`}
                  subtitle={associateTypeMap[associate.roleType].toLowerCase()}
                  {...associatePriceCart!}
                  mask={true}
                  className={`associate-item-${index}`}
                />
              );
            })}
        </Stack>
        {familyUpgradeCost && (
          <ReviewItem className="family-upgrade" title="Family upgrade fee" {...familyUpgradeCost!} />
        )}

        {/* RV item */}
        {hasRV &&
          (() => {
            const productPriceCart = sessionData.priceCart?.items?.find((a) => a.code === CartProductCode.RV);
            return <ReviewItem title={productPriceCart?.description!} {...productPriceCart!} className="rv-title" />;
          })()}
      </Stack>
      <Divider variant="fullWidth" sx={{ margin: '1rem 0' }} />
    </Stack>
  );
};
